import React, { useEffect } from 'react';
import {
  Link,
  Navigate,
  useParams
} from 'react-router-dom';
import { useAcademy } from '../context/AcademyContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import { formatDateRange } from '../utils/campLogic';

function UpcomingClass({ run, session }) {
  return (
    <div className="min-h-screen bg-cream px-4 py-12">
      <div className="mx-auto flex min-h-[75vh] max-w-4xl items-center justify-center">
        <div className="w-full overflow-hidden rounded-2xl border-2 border-tan bg-white text-center shadow-xl">
          <div className="bg-dark-green px-8 py-5 text-white">
            <div className="flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-amber">
              <SafeIcon name="Clock" />
              Class Upcoming
            </div>
          </div>

          <div className="px-8 py-14 md:px-16">
            <div className="mx-auto mb-7 flex h-24 w-24 items-center justify-center rounded-full bg-[#EAF1E6] text-forest-green">
              <SafeIcon name="BookOpen" className="text-5xl" />
            </div>

            <p className="mb-3 text-sm font-bold uppercase tracking-[0.16em] text-amber">
              Enrollment is open
            </p>
            <h1 className="text-3xl font-bold text-dark-green md:text-4xl">
              Welcome to GospelWise Author Academy!
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-muted">
              You are enrolled in{' '}
              <strong className="text-dark-green">
                {run?.title || run?.name || session?.title || 'this Academy session'}
              </strong>.
              Class is upcoming and opening soon. Your session dashboard and
              learning modules will become available when your Academy director
              turns the class on.
            </p>

            {(run?.start_date || run?.end_date) && (
              <div className="mx-auto mt-8 inline-flex items-center gap-3 rounded-full border border-tan bg-cream px-5 py-3 font-semibold text-dark-green">
                <SafeIcon name="Calendar" className="text-amber" />
                <span>
                  {formatDateRange(run.start_date, run.end_date)}
                </span>
              </div>
            )}

            <div className="mt-10">
              <Link
                to="/hub"
                className="inline-flex items-center gap-2 rounded-md bg-forest-green px-6 py-3 font-bold text-white transition-colors hover:bg-dark-green"
              >
                <SafeIcon name="ArrowLeft" />
                Return to Academy Hub
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SessionGate({ children }) {
  const { user } = useAuth();
  const { runSlug } = useParams();
  const {
    runs,
    currentRun,
    currentSession,
    currentDepartment,
    loadingAcademy,
    selectRun,
    userCanAccessRun,
    getRunSlug,
    isEnabled
  } = useAcademy();

  const safeUser = Array.isArray(user) ? user[0] : user;
  const routeRun = runs.find(
    (run) => getRunSlug(run) === runSlug
  ) ?? null;

  useEffect(() => {
    if (
      routeRun?.id &&
      String(currentRun?.id) !== String(routeRun.id)
    ) {
      selectRun(routeRun);
    }
  }, [currentRun?.id, routeRun, selectRun]);

  if (loadingAcademy) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-dark-green">
        <div className="text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mx-auto mb-3" />
          <p className="font-bold">Opening your learning center...</p>
        </div>
      </div>
    );
  }

  if (!routeRun || !userCanAccessRun(routeRun)) {
    return (
      <Navigate
        to="/hub"
        state={{ deniedRun: runSlug }}
        replace
      />
    );
  }

  if (
    String(currentRun?.id) !== String(routeRun.id) ||
    !currentSession ||
    !currentDepartment
  ) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-dark-green">
        <div className="text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mx-auto mb-3" />
          <p className="font-bold">Loading this event run...</p>
        </div>
      </div>
    );
  }

  if (!isEnabled(routeRun) && safeUser?.role !== 'admin') {
    return (
      <UpcomingClass
        run={routeRun}
        session={currentSession}
      />
    );
  }

  return (
    <>
      <Link
        to="/hub"
        className="fixed z-[60] right-4 bottom-4 md:right-6 md:bottom-6 flex items-center gap-2 bg-white border border-tan text-dark-green px-4 py-2.5 rounded-full shadow-lg hover:border-amber hover:text-amber transition-colors no-print"
      >
        <SafeIcon name="BookOpen" />
        <span className="text-sm font-bold">
          {currentDepartment?.name || 'Learning Center'}
          {' · '}
          {currentSession?.title || currentSession?.name || 'Session'}
          {currentRun?.title ? ` (${currentRun.title})` : ''}
        </span>
        <SafeIcon name="ChevronDown" className="text-xs" />
      </Link>
      {children}
    </>
  );
}